import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FILTERS } from "./constants/filters";
import { PinIcon, LeafIcon, CompassIcon } from "./FilterIcons";

const TABS = [
    { id: FILTERS.ESPACES, label: "Espaces", Icon: PinIcon },
    { id: FILTERS.ECO_CONCEPTION, label: "Eco-conception", Icon: LeafIcon },
    { id: FILTERS.CHASSE_AUX_SAVEURS, label: "Chasse aux saveurs", Icon: CompassIcon },
];

const FilterPills = ({ filters, changeFilter, showChasseAuxSaveurs }) => {
    const tabs = useMemo(
        () =>
            TABS.filter(
                (tab) => tab.id !== FILTERS.CHASSE_AUX_SAVEURS || showChasseAuxSaveurs,
            ),
        [showChasseAuxSaveurs],
    );

    const barRef = useRef(null);
    const textRefs = useRef({});
    const [box, setBox] = useState(null);
    const [animated, setAnimated] = useState(false);

    /* La géométrie est lue dans le DOM au lieu d'être dupliquée depuis le CSS :
       largeur naturelle de chaque libellé, et largeur d'une pill sans libellé.
       Toute pill inactive mesure exactement `base`, donc la pastille se place
       en `padLeft + index * base` — pas besoin de mesurer chaque onglet. */
    const measure = useCallback(() => {
        const bar = barRef.current;
        const pill = bar && bar.querySelector(".filter-pill");
        const icon = pill && pill.querySelector(".filter-pill-icon");
        if (!pill || !icon) return;

        const pillStyle = getComputedStyle(pill);
        const labels = {};
        tabs.forEach(({ id }) => {
            const el = textRefs.current[id];
            if (el) labels[id] = el.getBoundingClientRect().width;
        });

        setBox({
            labels,
            base:
                parseFloat(pillStyle.paddingLeft) +
                parseFloat(pillStyle.paddingRight) +
                icon.getBoundingClientRect().width,
            padLeft: parseFloat(getComputedStyle(bar).paddingLeft),
        });
    }, [tabs]);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        let cancelled = false;
        const remeasure = () => {
            if (!cancelled) measure();
        };
        window.addEventListener("resize", remeasure);
        // Montserrat change la largeur du texte : re-mesurer une fois chargée
        if (document.fonts) document.fonts.ready.then(remeasure);
        return () => {
            cancelled = true;
            window.removeEventListener("resize", remeasure);
        };
    }, [measure]);

    // Transitions activées après la première peinture, pour ne pas rejouer
    // l'animation d'ouverture à chaque montage (retour depuis une fiche).
    useEffect(() => {
        const raf = requestAnimationFrame(() => setAnimated(true));
        return () => cancelAnimationFrame(raf);
    }, []);

    const activeIndex = tabs.findIndex((tab) => tab.id === filters);
    const labelWidth = (id) => (box && box.labels[id]) || 0;

    const thumb =
        box && activeIndex >= 0
            ? {
                  transform: `translateX(${box.padLeft + activeIndex * box.base}px)`,
                  width: `${box.base + labelWidth(tabs[activeIndex].id)}px`,
              }
            : null;

    return (
        <nav
            ref={barRef}
            className={animated ? "filter-pills is-animated" : "filter-pills"}
            aria-label="Filtres de la carte"
        >
            {thumb && (
                <span className="filter-pills-thumb" style={thumb} aria-hidden="true" />
            )}
            {tabs.map(({ id, label, Icon }, index) => {
                const isActive = index === activeIndex;
                return (
                    <button
                        key={id}
                        type="button"
                        className={isActive ? "filter-pill active" : "filter-pill"}
                        aria-pressed={isActive}
                        onClick={() => changeFilter(id)}
                    >
                        <Icon className="filter-pill-icon" />
                        <span
                            className="filter-pill-label"
                            style={{ width: isActive ? `${labelWidth(id)}px` : 0 }}
                        >
                            <span
                                className="filter-pill-label-text"
                                ref={(el) => {
                                    textRefs.current[id] = el;
                                }}
                            >
                                {label}
                            </span>
                        </span>
                    </button>
                );
            })}
        </nav>
    );
};

export default FilterPills;
